<template>
  <div class="navigation-bar column no-wrap bg-white full-height">
    <rv-scroll-area
      class="navigation-bar__content fit"
      data-automation="navigation-bar__scroll-area"
      no-horizontal
    >
      <q-list
        class="navigation-bar__list column q-mr-md q-mt-lg"
        :class="{ 'navigation-bar__list--active': collapsed }"
      >
        <NavigationBarProjects
          :collapsed-after-animation="collapsedAfterAnimation"
          :collapsed-navigationbar="collapsed"
        />

        <NavigationBarPlugins
          v-if="$features.showPlugins"
          :collapsed-after-animation="collapsedAfterAnimation"
          :collapsed-navigationbar="collapsed"
        />
      </q-list>
    </rv-scroll-area>

    <div
      class="navigation-bar__footer column q-py-lg"
      :class="{'navigation-bar__footer--collapsible': menuItems.length > 1 }"
    >
      <template v-if="$can('read', 'Inference') && $features.showPlatformToPlatformConnection">
        <NavigationBarLink
          v-if="!collapsed"
          :collapsed-after-animation="collapsedAfterAnimation"
          :collapsed-navigationbar="collapsed"
          icon="rvai-base:topology"
          :label="!collapsed ?
            $t('navigationBar.topology')
            : ''"
          :to="{ name: $routes.PlatformConnections }"
        />
        <rv-action-button
          v-else
          data-automation="navigationBar__topology"
          icon="rvai-base:topology"
          :selected="isPlatformConnectionsActive"
          @click="navigateTo($routes.PlatformConnections)"
        >
          <rv-tooltip
            anchor="center right"
            arrow="left"
            data-automation="navigationBar__topology-tooltip"
            self="center left"
          >
            {{ $t('navigationBar.topology') }}
          </rv-tooltip>
        </rv-action-button>
      </template>

      <template v-if="$can('read', 'Inference')">
        <NavigationBarLink
          v-if="!collapsed"
          :collapsed-after-animation="collapsedAfterAnimation"
          :collapsed-navigationbar="collapsed"
          icon="custom:inference-monitoring"
          :label="!collapsed
            ? $t('navigationBar.inferenceMonitoring')
            : ''"
          :to="{ name: $routes.InferenceMonitoring }"
        />
        <rv-action-button
          v-else
          data-automation="navigationBar__inference-monitoring"
          icon="custom:inference-monitoring"
          :selected="isInferenceMonitoringActive"
          @click="navigateTo($routes.InferenceMonitoring)"
        >
          <rv-tooltip
            anchor="center right"
            arrow="left"
            data-automation="navigationBar__inference-monitoring-tooltip"
            self="center left"
          >
            {{ $t(t('navigationBar.inferenceMonitoring')) }}
          </rv-tooltip>
        </rv-action-button>
      </template>

      <template v-if="!collapsed">
        <NavigationBarLink
          v-if="menuItems.length === 1 && menuItems[0]"
          :key="menuItems[0].label"
          :icon="menuItems[0].icon ?? adminDashboardIcon"
          :label="menuItems[0].label"
          :to="menuItems[0].to"
        />
        <rv-expansion-item
          v-else
          v-model="expandedMenu"
          class="q-px-lg"
          data-automation="admin-dashboard"
          right-icon
        >
          <template #label>
            <div class="navigation-bar__item__admin-dashboard row no-wrap items-center">
              <q-icon
                name="custom:admin-dashboard"
                size="1.5rem"
              />
              <div class="q-ml-md text-subtitle3 text-no-wrap">
                {{ $t('navigationBar.system') }}
              </div>
              <q-icon
                v-if="(!isLicenseValid || !isNormalStorageStatus) && !expandedMenu"
                class="q-ml-sm"
                :color="alertColor"
                name="mdi-alert-circle"
                size="1.5rem"
              />
            </div>
          </template>
          <NavigationBarLink
            v-for="item in menuItems"
            :key="item.label"
            :appended-icon="item.appendedIcon"
            :appended-icon-color="item.appendedIconColor"
            :label="item.label"
            :to="item.to"
          />
        </rv-expansion-item>
      </template>
      <template v-else>
        <div v-if="menuItems.length === 1 && menuItems[0]">
          <NavigationBarLink
            :key="menuItems[0].label"
            :collapsed-after-animation="collapsedAfterAnimation"
            :collapsed-navigationbar="collapsed"
            :icon="menuItems[0].icon ?? adminDashboardIcon"
            :label="menuItems[0].label"
            :to="menuItems[0].to"
          />
          <rv-tooltip
            v-if="!showMenu"
            anchor="center right"
            arrow="left"
            data-automation="navigation-bar__admin-dashboard-tooltip"
            self="center left"
          >
            {{ menuItems[0].label }}
          </rv-tooltip>
        </div>
        <rv-action-button
          v-else
          data-automation="navigationBar__admin-dashboard"
          :icon="adminDashboardIcon"
          :selected="isAdminDashboardActive"
          :style="{'--alert-color': `var(--q-${alertColor})`}"
          tertiary
        >
          <q-menu
            v-if="menuItems.length > 1"
            v-model="showMenu"
            anchor="center right"
            auto-close
            :offset="[32,0]"
            self="center left"
          >
            <q-list separator>
              <NavigationBarLink
                v-for="(item, index) in menuItems"
                :key="item.label"
                :appended-icon="item.appendedIcon"
                :appended-icon-color="item.appendedIconColor"
                collapsed-navigationbar-menu
                :label="item.label"
                :to="item.to"
                :with-border="index > 1"
                with-padding
              />
            </q-list>
          </q-menu>
          <rv-tooltip
            v-if="!showMenu"
            anchor="center right"
            arrow="left"
            data-automation="navigation-bar__admin-dashboard-tooltip"
            self="center left"
          >
            {{ $t('navigationBar.system') }}
          </rv-tooltip>
        </rv-action-button>
      </template>

      <div>
        <a
          class="navigation-bar__item text-black-1 row no-wrap items-center q-py-sm q-px-lg"
          data-automation="navigation-bar__help"
          :href="docsUrl"
          target="_blank"
          @click.prevent="openDocs"
        >
          <q-icon
            class="navigation-bar__icon"
            name="mdi-help-circle"
            size="1.5rem"
          />

          <Transition name="text">
            <div
              v-if="!collapsed"
              class="q-ml-md text-subtitle3"
            >
              {{ $t('navigationBar.help.title') }}
            </div>
          </Transition>
        </a>

        <rv-tooltip
          anchor="center right"
          arrow="left"
          data-automation="navigation-bar__help"
          self="center left"
        >
          {{ $t('navigationBar.help.hint') }}
        </rv-tooltip>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useDocs } from '~composables/useDocs'
import { useDocumentTitle } from '~composables/useDocumentTitle'
import router from '~router'
import { RouteNames } from '~router/routeNames'
import { ability } from '~services/ability'
import {
  features,
  InferenceCenterMode,
} from '~services/features'
import { useLicensing } from '~stores/composables/useLicensing'
import { useStorages } from '~stores/composables/useStorages'
import NavigationBarLink from './NavigationBarLink.vue'
import NavigationBarPlugins from './NavigationBarPlugins.vue'
import NavigationBarProjects from './NavigationBarProjects.vue'


const adminDashboardNames = [
  RouteNames.Settings,
  RouteNames.About,
  RouteNames.Resources,
  RouteNames.Members,
  RouteNames.Classes,
  RouteNames.CameraManagement,
  RouteNames.Cameras,
  RouteNames.CameraConfigurations,
  RouteNames.CameraConfigurationsSetup,
]

const colorsByPriority = [
  'negative',
  'warning',
]

const navigationNames = [
  RouteNames.InferenceMonitoring,
  RouteNames.PlatformConnections,
  ...adminDashboardNames,
]

withDefaults(defineProps<{
  collapsed?: boolean
  collapsedAfterAnimation?: boolean
}>(), {
  collapsed: false,
  collapsedAfterAnimation: false,
})

const {
  docsUrl,
  openDocs,
} = useDocs()
const { t } = useI18n()
const {
  isLicenseValid,
  licenseColor,
} = useLicensing()
const {
  isNormalStorageStatus,
  storageColor,
} = useStorages()
const { setDocumentTitle } = useDocumentTitle()

const { currentRoute } = useRouter()

const showMenu = ref(false)
const expandedMenu = ref(false)

const showCamerasMenuOption = features.inferenceCenterMode !== InferenceCenterMode.NoExampleStreamDependency

const menuItems = computed(() => [
  ability.can('crud', 'Settings') && {
    label: t('navigationBar.settings'),
    to: { name: RouteNames.Settings },
  },
  {
    appendedIcon: isLicenseValid.value ? '' : 'mdi-alert-circle',
    appendedIconColor: licenseColor.value,
    icon: 'mdi-information',
    label: t('navigationBar.about'),
    to: { name: RouteNames.About },
  },
  ability.can('crud', 'Class') && {
    label: t('navigationBar.classes'),
    to: { name: RouteNames.Classes },
  },
  ability.can('read', 'Member') && {
    label: t('navigationBar.members'),
    to: { name: RouteNames.Members },
  },
  ability.can('read', 'Resource') && {
    appendedIcon: isNormalStorageStatus.value ? '' : 'mdi-alert-circle',
    appendedIconColor: storageColor.value,
    label: t('navigationBar.resources'),
    to: { name: RouteNames.Resources },
  },
  ability.can('read', 'Camera') && showCamerasMenuOption && (
    {
      label: t('navigationBar.cameraManagement'),
      to: { name: RouteNames.CameraManagement },
    }
  ),
].filter(Boolean))

const isActivePath = computed<boolean>(() =>
  navigationNames.some(name => name === currentRoute.value.name))

const isAdminDashboardActive = computed<boolean>(() =>
  adminDashboardNames.some(name => name === currentRoute.value.name))

const isInferenceMonitoringActive = computed<boolean>(() =>
  currentRoute.value.name === RouteNames.InferenceMonitoring)

const isPlatformConnectionsActive = computed<boolean>(() =>
  currentRoute.value.name === RouteNames.PlatformConnections)

const adminDashboardIcon = computed<string>(() => {
  return `custom:admin-dashboard${isLicenseValid.value && isNormalStorageStatus.value ? '' : '-alert'}`
})

const alertColor = computed(() => {
  const colors = [
    licenseColor.value,
    storageColor.value,
  ]
  let color: string | undefined

  for (let i = 0; i < colorsByPriority.length; i++) {
    if (colors.some(c => c === colorsByPriority[i]) && !color) {
      color = colorsByPriority[i]
      break
    }
  }

  return color || 'positive'
})

expandedMenu.value = isAdminDashboardActive.value

function getRouteTitle () {
  switch (currentRoute.value.name) {
    case RouteNames.About:
      return t('navigationBar.about')
    case RouteNames.CameraManagement:
    case RouteNames.CameraConfigurations:
    case RouteNames.CameraConfigurationsSetup:
    case RouteNames.Cameras:
      return t('navigationBar.cameraManagement')
    case RouteNames.Classes:
      return t('navigationBar.classes')
    case RouteNames.InferenceMonitoring:
      return t('navigationBar.inferenceMonitoring')
    case RouteNames.PlatformConnections:
      return t('navigationBar.topology')
    case RouteNames.Members:
      return t('navigationBar.members')
    case RouteNames.Resources:
      return t('navigationBar.resources')
    case RouteNames.Settings:
      return t('navigationBar.settings')
    default:
      return t('navigationBar.system')
  }
}

function navigateTo (route: RouteNames): void {
  void router.push({ name: route })
}

watch([isActivePath, () => currentRoute.value.name], () => {
  if (isActivePath.value) {
    setDocumentTitle(getRouteTitle())
  }
}, { immediate: true })
</script>

<style lang="sass">
$transition-duration: .15s

.icon-enter-active,
.icon-leave-active
  transition: opacity $transition-duration ease-out

.icon-enter-from,
.icon-leave-to
  opacity: 0

.text-enter-active,
.text-leave-active
  transition: opacity $transition-duration ease-out

.text-enter-active
  transition-delay: .3s // left sidebar animation duration

.text-enter-from,
.text-leave-to
  opacity: 0
</style>

<style lang="sass" scoped>
@import '../css/quasar.variables'

$transition-duration: .15s

.navigation-bar
  overflow: hidden

  &__footer
    gap: 8px
    flex: 0 0 auto
    border-top: 1px solid var(--q-grey-3)

    &--collapsible
      :deep(.action)
        align-items: flex-start
        width: 32px
        padding-left: 16px // hack to stop icon from jumping around when collapsing navbar

      :deep(.q-btn)
        height: 40px !important
        width: 40px !important

  .badge
    top: unset
    right: 9px
    bottom: 9px
    padding: 4px
    min-height: 3px
    z-index: 1
    border: 1px solid white
    opacity: 1

  &__logo
    height: 40px
    width: auto

    > :deep(img)
      height: auto
      width: auto

  &__content
    overflow: hidden

  &__list
    transition-delay: $transition-duration
    gap: 16px

    &--active
      gap: 24px

  &__item
    &:hover
      color: var(--q-primary) !important

    &__admin-dashboard
      height: 40px !important
      align-items: center
</style>
